import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { useTranslation } from "react-i18next";
import { department } from "../../../data/db";

import { repliValidator } from "../../../components";

import useStyles from "./MyAccount.style";
import RepLITextFieldSkeleton from "../../../components/RepLITextFieldSkeleton";
import Cookies from "universal-cookie";
import { ProviderSelect } from "../../mobile/profiler/InputComponents";

const cookies = new Cookies();

const MyAccountTextField = (props) => {
  return <TextField variant="outlined" margin="normal" fullWidth {...props} />;
};

const MyAccountSkeleton = () => {
  return (
    <>
      <div style={{ width: 200 }}>
        <RepLITextFieldSkeleton />
      </div>
      <div style={{ width: 500 }}>
        <RepLITextFieldSkeleton />
        <RepLITextFieldSkeleton />
        <RepLITextFieldSkeleton />
        <RepLITextFieldSkeleton />
      </div>
    </>
  );
};

const MyAccount = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [saveResult, setSaveResult] = useState();
  const [organizationsData, setOrganizationsData] = useState([])
  const [organizationList, setOrganizationList] = useState([])
  const [id, setId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [departmentValue, setDepartmentValue] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [jobTitle, setJobTitle] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [availableEmail, setAvailableEmail] = useState(true);
  const [emailUnavailbilityMessage, setEmailUnavailbilityMessage] = useState("");

  const MenuProps = {
    getContentAnchorEl: null,
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "center",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "center",
    },
    variant: "menu",
  };


  useEffect(() => {
    const getOrganizations = async () => {
      try {
        let response = await fetch(
          `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_ORGANIZATION}`,
          {
            headers: {
              "X-ACCESS-TOKEN": cookies.get("token"),
            },
          }
        );

        let json = await response.json();
        if (!response.ok) {
          console.log(json.message);
          return;
        }

        setOrganizationsData(json);
      } catch (error) {
        console.log(error);
        console.log(
          `${t(
            "module.provider.listOrganizations.message.fetchDataFailure"
          )}. ${t("common.message.error.seeLog")}.`
        );
      }
    };

    getOrganizations();
  }, [t]);

  useEffect(() => {
    if (organizationsData && organizationsData.length > 0) {
      setOrganizationList(organizationsData.map((org) => org.name));
    }
  }, [organizationsData]);

  const validator = useRef(
    repliValidator({
      validators: {
        password_confirmation: {
          message: t("common.message.validation.passwordConfirmation"),
          rule: (val, params, validator) => {
            return val === params[0];
          },
          required: true,
        },
      },
    })
  );

  useEffect(() => {
    const loadAccountInfo = async () => {
      try {
        let response = await fetch(
          `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_PROVIDER_AUTH_SIGNEDUSER}`,
          {
            headers: {
              "X-ACCESS-TOKEN": cookies.get("token"),
            },
          }
        );
        let json = await response.json();
        if (!response.ok) {
          setError(json.message);
          return;
        }
        setId(json._id);
        setFirstName(json.firstName);
        setLastName(json.lastName);
        setEmail(json.email);
        setRoles(json.roles);
        setOrganizations(json.organizations);
        setJobTitle(json.jobTitle);
        setDepartmentValue(json.department);
        validator.current.showMessages();
      } catch (error) {
        console.log(error);
        setError(
          `${t("module.provider.myAccount.message.fetchDataFailure")}. ${t(
            "common.message.error.seeLog"
          )}.`
        );
      } finally {
        setLoading(false);
      }
    };

    loadAccountInfo();
  }, [t]);

  const getPostParameters = () => {
    let params = new URLSearchParams({
      id: id,
      firstName: firstName,
      lastName: lastName,
      email: email,
      organizations: organizations,
      jobTitle: jobTitle,
      department: departmentValue,
      roles: roles,
    });
    if (password) {
      params.append("password", password);
      params.append("passwordConfirmation", passwordConfirmation);
    }
    return params;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validator.current.allValid();
    if (!isValid) return;
    setSending(true);
    try {
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_USER_MYACCOUNT}`,
        {
          method: "post",
          headers: {
            "X-ACCESS-TOKEN": cookies.get("token"),
          },
          body: getPostParameters(),
        }
      );
      if (response.ok) {
        setSaveResult({
          severity: "success",
          message: t("module.provider.myAccount.message.editAccountSuccess"),
        });
        setPassword("");
        setPasswordConfirmation("");
      } else {
        let json = await response.json();
        setSaveResult({ severity: "error", message: json.message });
      }
    } catch (error) {
      console.log(error);
      setSaveResult({
        severity: "error",
        message: `${t(
          "module.provider.myAccount.message.editAccountFailure"
        )}. ${t("common.message.error.seeLog")}.`,
      });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    setEmailUnavailbilityMessage(
      availableEmail
        ? ""
        : t("module.provider.addPatient.message.duplicatedEmail")
    );
  }, [availableEmail, t]);

  const checkDuplicatedEmail = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_PATIENT_GETBYEMAIL
        }?${new URLSearchParams({
          email: email,
          id: id,
        })}`,
        {
          headers: {
            "X-ACCESS-TOKEN": cookies.get("token"),
          },
        }
      );
      if (!response.ok) {
        setAvailableEmail(false);
        props.validator.showMessageFor("email");
      } else {
        setAvailableEmail(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  if (loading) return <MyAccountSkeleton />;

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box className={classes.root}>
      <Typography variant="h4">
        {t("module.provider.myAccount.title")}
      </Typography>
      {saveResult && (
        <Alert severity={saveResult.severity}>{saveResult.message}</Alert>
      )}
      <form className={classes.form} onSubmit={handleSubmit}>
        <MyAccountTextField
          label={`${t("common.form.label.firstName")}*`}
          autoComplete="firstname"
          autoFocus
          error={!validator.current.fieldValid("firstName")}
          helperText={validator.current.message(
            "firstName",
            firstName,
            "required"
          )}
          onChange={(e) => setFirstName(e.target.value)}
          value={firstName}
        />

        <MyAccountTextField
          label={`${t("common.form.label.lastName")}*`}
          autoComplete="lastname"
          error={!validator.current.fieldValid("lastName")}
          helperText={validator.current.message(
            "lastName",
            lastName,
            "required|alpha"
          )}
          onChange={(e) => setLastName(e.target.value)}
          value={lastName}
        />

        <MyAccountTextField
          label={`${t("common.form.label.email")}*`}
          autoComplete="email"
          // InputProps={{ readOnly: true }}
          error={!validator.current.fieldValid("email") || !availableEmail}
          helperText={
            validator.current.message("email", email, "required|email") ||
            emailUnavailbilityMessage
          }
          onChange={(e) => setEmail(e.target.value)}
          onBlur={checkDuplicatedEmail}
          value={email}
        />

        <MyAccountTextField
          InputProps={!roles.includes("administrator") && { readOnly: true }}
          label={`${t("common.form.label.jobTitle")}*`}
          autoComplete="jobTitle"
          onChange={(e) => setJobTitle(e.target.value.trim())}
          value={jobTitle}
        />

        <FormControl
          id="organization"
          fullWidth
          variant="outlined"
          style={{ margin: "16px 0 8px 0" }}
        >
          <InputLabel
          >
            Organization
          </InputLabel>
          <Select
            label="Organization"
            multiple
            value={organizations}
            onChange={(e) => {
              setSaveResult();
              if (e.target.value.length > 3) {
                setSaveResult({
                  severity: "error",
                  message: "Maximum three Organizations only.",
                });
              } else {
                setOrganizations(e.target.value);
              }
            }}
            inputProps={{
              name: "organization",
              id: "organization",
            }}
            renderValue={(selected) => selected.join(", ")}
            MenuProps={MenuProps}

          >
            {organizationList.map((option, i) => (
              <MenuItem key={i} value={option}>
                <ListItemIcon>
                  <Checkbox
                    color="primary"
                    checked={organizations.indexOf(option) > -1}
                  />
                </ListItemIcon>
                <ListItemText primary={option} />
              </MenuItem>
            ))}
          </Select>

        </FormControl>


        <ProviderSelect
          id="department"
          label={`${t("common.form.label.department")}*`}
          value={departmentValue}
          items={department}
          fullWidth
          margin="normal"
          onChange={(e) => setDepartmentValue(e.target.value)}
          disabled={!roles.includes("administrator") && "true"}
        />

        <MyAccountTextField
          label={t("common.form.label.password")}
          autoComplete="password"
          type="password"
          error={!validator.current.fieldValid("password")}
          helperText={
            validator.current.message("password", password, "min:8") ||
            t("module.provider.editUser.message.passwordHelpText")
          }
          onChange={(e) => setPassword(e.target.value.trim())}
          value={password}
        />
        <MyAccountTextField
          label={t("common.form.label.passwordConfirmation")}
          autoComplete="passwordconfirmation"
          type="password"
          error={!validator.current.fieldValid("passwordConfirmation")}
          helperText={validator.current.message(
            "passwordConfirmation",
            passwordConfirmation,
            `password_confirmation:${password}`
          )}
          onChange={(e) => setPasswordConfirmation(e.target.value.trim())}
          value={passwordConfirmation}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={sending}
          style={{ margin: '5% 0 10% 0' }}
        >
          {t("common.form.button.save")}
          {sending && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </Button>
      </form>
    </Box>
  );
};

export default MyAccount;
